// src/pages/Members.jsx
import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { Card, PageHeader, Table, TR, TD, Badge, Btn, Flash, Spinner, Empty, Modal, Field, Input, Select } from '../components/UI';

const STATUS_COLOR = { active: 'green', inactive: 'amber', resigned: 'red' };

export default function Members() {
  const [members, setMembers]       = useState([]);
  const [filtered, setFiltered]     = useState([]);
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatus]   = useState('');
  const [loading, setLoading]       = useState(true);
  const [flash, setFlash]           = useState(null);
  const [editMember, setEditMember] = useState(null);
  const [editForm, setEditForm]     = useState({});
  const [editSaving, setEditSaving] = useState(false);

  function load() {
    setLoading(true);
    // Always load all members — filter client-side so counts stay accurate
    api.get('/api/members').then(r => {
      if (r?.ok) setMembers(r.data.members || []);
      setLoading(false);
    });
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(members.filter(m => {
      const matchSearch = !q ||
        m.lastname.toLowerCase().includes(q) ||
        (m.parents || '').toLowerCase().includes(q) ||
        (m.caregiver || '').toLowerCase().includes(q) ||
        m.membership_number.includes(q);
      const s = m.status || (m.resigned ? 'resigned' : 'active');
      const matchStatus = !statusFilter || s === statusFilter;
      return matchSearch && matchStatus;
    }));
  }, [search, members, statusFilter]);

  async function setMemberStatus(num, status) {
    const r = await api.post(`/api/member/${num}/set-status`, { status });
    if (r?.ok) {
      setMembers(prev => prev.map(m => m.membership_number === num ? { ...m, status } : m));
      setFlash({ type: 'success', msg: `Status updated to ${status}.` });
    }
  }

  function openEdit(m) {
    setEditMember(m);
    setEditForm({
      lastname: m.lastname || '',
      membership_type: m.membership_type || 'Family',
      parents: m.parents || '',
      children: m.children || '',
      caregiver: m.caregiver || '',
      total_people: m.total_people || 1,
    });
  }

  async function saveEdit() {
    setEditSaving(true);
    const r = await api.post(`/api/member/${editMember.membership_number}/update`, editForm);
    setEditSaving(false);
    if (r?.ok) {
      setMembers(prev => prev.map(m => m.membership_number === editMember.membership_number ? { ...m, ...editForm } : m));
      setFlash({ type: 'success', msg: `${editForm.lastname} updated.` });
      setEditMember(null);
    } else {
      setFlash({ type: 'error', msg: r?.data?.error || 'Save failed.' });
    }
  }

  async function deleteMember(num, lastname) {
    if (!window.confirm(`Delete ${lastname} (#${num})? This removes the member and all sign-in history. This cannot be undone.`)) return;
    const r = await api.post(`/api/member/${num}/delete`, {});
    if (r?.ok) {
      setMembers(prev => prev.filter(m => m.membership_number !== num));
      setFlash({ type: 'success', msg: `${lastname} deleted.` });
    }
  }

  const counts = members.reduce((a, m) => { a[m.status] = (a[m.status] || 0) + 1; return a; }, {});

  return (
    <>
      <div style={{ padding: '20px 16px', maxWidth: 1200, margin: '0 auto' }}>
      <PageHeader title="Member Status" subtitle={`${filtered.length} of ${members.length} shown — click a status button to update immediately`} />

      {flash && <Flash type={flash.type} onDismiss={() => setFlash(null)}>{flash.msg}</Flash>}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="text" placeholder="Search name or Mbr #" value={search} onChange={e => setSearch(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--fg)', fontSize: 13, width: 220 }} />
        {[
          { value: '', label: `All (${members.length})` },
          { value: 'active', label: `Active (${counts.active || 0})` },
          { value: 'inactive', label: `Inactive (${counts.inactive || 0})` },
          { value: 'resigned', label: `Resigned (${counts.resigned || 0})` },
        ].map(f => (
          <button key={f.value} onClick={() => setStatus(f.value)} style={{
            padding: '7px 12px', fontSize: 12, fontWeight: 'bold', cursor: 'pointer',
            border: '1px solid var(--border)', fontFamily: 'Arial, sans-serif',
            background: statusFilter === f.value ? 'var(--accent)' : 'var(--bg2)',
            color: statusFilter === f.value ? '#fff' : 'var(--fg2)',
          }}>{f.label}</button>
        ))}
      </div>

      <Card>
        {loading ? <Spinner /> : filtered.length === 0 ? <Empty message="No members found." /> : (
          <Table headers={['MBR #', 'Last Name', 'Type', 'Parents', 'Children', 'Caregiver', 'Status', 'Set Status']}>
            {filtered.map(m => {
              const s = m.status || (m.resigned ? 'resigned' : 'active');
              return (
                <React.Fragment key={m.id}>
                <TR>
                  <TD style={{ fontFamily: 'monospace' }}>{m.membership_number}</TD>
                  <TD style={{ fontWeight: 'bold', color: 'var(--fg)' }}>{m.lastname}</TD>
                  <TD>{m.membership_type}</TD>
                  <TD style={{ fontSize: 12 }}>{m.parents || '—'}</TD>
                  <TD style={{ fontSize: 12 }}>{m.children || '—'}</TD>
                  <TD style={{ fontSize: 12 }}>{m.caregiver || '—'}</TD>
                  <TD><Badge variant={STATUS_COLOR[s]}>{s}</Badge></TD>
                  <TD>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <Btn variant={s === 'active' ? 'primary' : 'ghost'} style={{ fontSize: 11, padding: '4px 8px', opacity: s === 'active' ? 1 : 0.6 }}
                        onClick={() => s !== 'active' && setMemberStatus(m.membership_number, 'active')}>Active</Btn>
                      <Btn variant={s === 'inactive' ? 'primary' : 'ghost'} style={{ fontSize: 11, padding: '4px 8px', opacity: s === 'inactive' ? 1 : 0.6 }}
                        onClick={() => s !== 'inactive' && setMemberStatus(m.membership_number, 'inactive')}>Inactive</Btn>
                      <Btn variant={s === 'resigned' ? 'primary' : 'ghost'} style={{ fontSize: 11, padding: '4px 8px', opacity: s === 'resigned' ? 1 : 0.6 }}
                        onClick={() => s !== 'resigned' && setMemberStatus(m.membership_number, 'resigned')}>Resigned</Btn>
                      <Btn variant="outline" style={{ fontSize: 11, padding: '4px 8px' }}
                        onClick={() => openEdit(m)}>Edit</Btn>
                      <Btn variant="danger" style={{ fontSize: 11, padding: '4px 8px' }}
                        onClick={() => deleteMember(m.membership_number, m.lastname)}>Delete</Btn>
                    </div>
                  </TD>
                </TR>
                {m.caregiver && (
                  <TR key={`${m.id}-cg`} style={{ background: 'var(--bg2)', opacity: 0.85 }}>
                    <TD style={{ fontFamily: 'monospace', color: 'var(--fg2)' }}>{m.membership_number}</TD>
                    <TD style={{ fontWeight: 'bold', color: 'var(--fg)' }}>{m.lastname}</TD>
                    <TD style={{ color: 'var(--fg2)', fontSize: 11 }}>Caregiver</TD>
                    <TD style={{ fontSize: 12 }}>{m.caregiver}</TD>
                    <TD colSpan={4} style={{ fontSize: 11, color: 'var(--fg2)', fontStyle: 'italic' }}>
                      Caregiver for the {m.lastname} family
                    </TD>
                  </TR>
                )}
                </React.Fragment>
              );
            })}
          </Table>
        )}
      </Card>
    </div>

      {editMember && (
        <Modal title={`Edit — ${editMember.lastname} #${editMember.membership_number}`} onClose={() => setEditMember(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <Field label="Last Name"><Input value={editForm.lastname} onChange={e => setEditForm(f => ({ ...f, lastname: e.target.value }))} /></Field>
            <Field label="Type">
              <Select value={editForm.membership_type} onChange={e => setEditForm(f => ({ ...f, membership_type: e.target.value }))}>
                <option>Family</option><option>Single</option><option>Couple</option><option>Junior</option>
              </Select>
            </Field>
            <Field label="Members / Parents" style={{ gridColumn: '1/-1' }}><Input value={editForm.parents} onChange={e => setEditForm(f => ({ ...f, parents: e.target.value }))} placeholder="e.g. John, Jane" /></Field>
            <Field label="Children" style={{ gridColumn: '1/-1' }}><Input value={editForm.children} onChange={e => setEditForm(f => ({ ...f, children: e.target.value }))} placeholder="e.g. Tom, Emma, Lucy" /></Field>
            <Field label="Caregiver" style={{ gridColumn: '1/-1' }}><Input value={editForm.caregiver} onChange={e => setEditForm(f => ({ ...f, caregiver: e.target.value }))} placeholder="Optional" /></Field>
            <Field label="Total People"><Input type="number" min={1} value={editForm.total_people} onChange={e => setEditForm(f => ({ ...f, total_people: parseInt(e.target.value) || 1 }))} /></Field>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn onClick={saveEdit} disabled={editSaving}>{editSaving ? 'Saving...' : 'Save Changes'}</Btn>
            <Btn variant="ghost" onClick={() => setEditMember(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </>
  );
}
